import React from "react";
import styled, { keyframes } from "styled-components";
import { Bell, BellOff, Settings, ExternalLink } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useNotification } from "../contexts/notification";

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  background-color: #fff;
  padding: 28px 24px 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border-top: 3px solid #c9a961;
  animation: ${fadeInScale} 0.25s ease-out;
  margin: 0 16px;
`;

const IconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${(props) => (props.$variant === "denied" ? "#fee" : "rgba(201, 169, 97, 0.1)")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 20px;
  text-align: center;
  line-height: 1.5;
`;

const StepList = styled.ol`
  margin: 0 0 20px;
  padding: 0 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StepItem = styled.li`
  font-size: 13px;
  color: #555;
  line-height: 1.5;
`;

const StepHighlight = styled.span`
  font-weight: 600;
  color: #333;
`;

const PrimaryButton = styled.button`
  width: 100%;
  border-radius: 10px;
  background-color: #c9a961;
  padding: 13px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: #b89451;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  border-radius: 10px;
  background-color: transparent;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #888;
  border: none;
  cursor: pointer;
  margin-top: 8px;
  transition: color 0.2s;

  &:hover {
    color: #555;
  }
`;

const DISMISS_KEY_PREFIX = "hebe_notif_prompt_dismissed_";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const isDismissed = (buildingCode) => {
  if (typeof window === "undefined") return true;
  try {
    const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${buildingCode || "default"}`);
    if (!raw) return false;
    return Date.now() < Number(raw);
  } catch (_e) {
    return false;
  }
};

const setDismissed = (buildingCode) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      `${DISMISS_KEY_PREFIX}${buildingCode || "default"}`,
      String(Date.now() + DISMISS_TTL_MS),
    );
  } catch (_e) {
    // ignore
  }
};

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "desktop_chrome";
  const ua = navigator.userAgent || "";

  // iOS detection (iPhone, iPad, iPod, or Mac with touch)
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) {
    return "ios_safari";
  }

  // Android
  if (/Android/i.test(ua)) {
    if (/Firefox/i.test(ua)) return "android_firefox";
    return "android_chrome";
  }

  // Desktop
  if (/Firefox/i.test(ua)) return "desktop_firefox";
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return "desktop_safari";
  return "desktop_chrome";
};

const getBuildingCode = () =>
  typeof window !== "undefined"
    ? window.location.pathname.match(/\/hebe\/([^/]+)\/hgs/)?.[1] || null
    : null;

/**
 * Prompt modal — shown automatically for fresh browsers ("prompt" state)
 * or triggered manually for "denied" state (from notification toggle).
 *
 * Props:
 *   mode: "prompt" | "denied"
 *   onClose: () => void
 */
export const NotificationPromptModal = ({ mode, onClose }) => {
  const { t } = useLanguage();
  const { subscribe } = useNotification();
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const platform = React.useMemo(detectPlatform, []);

  const handleEnable = async () => {
    setIsSubscribing(true);
    try {
      await subscribe();
    } finally {
      setIsSubscribing(false);
      onClose();
    }
  };

  const handleDismiss = () => {
    const buildingCode = getBuildingCode();
    setDismissed(buildingCode);
    onClose();
  };

  if (mode === "denied") {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <IconCircle $variant="denied">
            <BellOff size={26} color="#c00" />
          </IconCircle>
          <Title>{t.notifications.deniedModal.title}</Title>
          <Description>{t.notifications.deniedModal.description}</Description>
          <StepList>
            {getDeniedSteps(platform, t).map((step, idx) => (
              <StepItem key={idx}>{step}</StepItem>
            ))}
          </StepList>
          <PrimaryButton onClick={onClose}>
            <Settings size={16} />
            {t.notifications.deniedModal.gotIt}
          </PrimaryButton>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // mode === "prompt" — fresh browser, ask to enable
  return (
    <ModalOverlay onClick={handleDismiss}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <IconCircle>
          <Bell size={26} color="#c9a961" />
        </IconCircle>
        <Title>{t.notifications.promptModal.title}</Title>
        <Description>{t.notifications.promptModal.description}</Description>
        <PrimaryButton onClick={handleEnable} disabled={isSubscribing}>
          <Bell size={16} />
          {isSubscribing
            ? t.notifications.promptModal.enabling
            : t.notifications.promptModal.enable}
        </PrimaryButton>
        <SecondaryButton onClick={handleDismiss}>
          {t.notifications.promptModal.later}
        </SecondaryButton>
      </ModalContent>
    </ModalOverlay>
  );
};

/**
 * Hook to auto-show the prompt modal on a page for fresh browsers.
 * Returns { showPromptModal, promptMode, closePromptModal }.
 */
export const useNotificationPrompt = () => {
  const { notificationState } = useNotification();
  const [promptMode, setPromptMode] = React.useState(null);

  // Auto-show for "prompt" state on mount (fresh browser, not dismissed)
  React.useEffect(() => {
    if (notificationState === "prompt") {
      const buildingCode = getBuildingCode();
      if (!isDismissed(buildingCode)) {
        // Small delay so the page renders first
        const timer = setTimeout(() => setPromptMode("prompt"), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [notificationState]);

  const showDeniedModal = React.useCallback(() => {
    setPromptMode("denied");
  }, []);

  const closePromptModal = React.useCallback(() => {
    setPromptMode(null);
  }, []);

  return {
    promptMode,
    showDeniedModal,
    closePromptModal,
  };
};

function getDeniedSteps(platform, t) {
  const steps = t.notifications.deniedModal.steps;
  switch (platform) {
    case "ios_safari":
      return steps.ios;
    case "android_chrome":
      return steps.android;
    case "android_firefox":
      return steps.androidFirefox;
    case "desktop_firefox":
      return steps.desktopFirefox;
    case "desktop_safari":
      return steps.desktopSafari;
    default:
      return steps.desktopChrome;
  }
}
